import { useState, useCallback, useEffect } from 'react';
import { formFieldsData } from '../../../types/settings/FormFieldProps';
import { getFormFiled, getColumnById, saveColumnOrder } from '@/actions/fetch-form-fields';
import { toastMessage } from '@/utils/toast-message';
import ModernFormFieldTable from './ModernFormFieldTable';

import ModernSettingHeader from '../../../components/settings/ModernSettingHeader';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';
import { FormFieldsApiData } from '../../../types/settings/settings';
import CreateNewFieldForm from '../../../components/Forms/CreateNewFieldForm';
import { FormInput } from 'lucide-react';


export default function FormFieldsClientWrapper({
    initialData
}: FormFieldsApiData) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedField, setSelectedField] = useState<formFieldsData | null>(null);
    const [data, setData] = useState<formFieldsData[]>(initialData || []);
    const [isLoading, setIsLoading] = useState(false);
    const [, setIsFieldLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortColumn, setSortColumn] = useState<string>('col_order');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('ASC');
    const [isSavingOrder, setIsSavingOrder] = useState(false);

    const fetchData = useCallback(async (params: {
        search_term?: string,
        sort_column?: string,
        sort_order?: 'ASC' | 'DESC'
    } = {}) => {
        try {
            setIsLoading(true);
            const response = await getFormFiled({
                search_term: params.search_term,
                sort_column: params.sort_column || sortColumn,
                sort_order: params.sort_order || sortOrder
            });
            if (response.data) {
                console.log(response.data);

                setData(response.data.columns || []);
            }
        } catch (error: unknown) {
            if (error instanceof Error) {
                toastMessage(error.message, "error");
            } else {
                toastMessage("An unknown error occurred", "error");
            }
        } finally {
            setIsLoading(false);
        }
    }, [sortColumn, sortOrder]);

    // Initial data fetch on component mount
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOpenModal = async (field?: formFieldsData) => {
        if (field?.uuid) {
            console.log('uuid there:', field.uuid);

            // For edit mode, fetch the complete field data with options
            setIsFieldLoading(true);
            try {
                const response = await getColumnById(field.uuid);
                console.log('response from the FormFieldCWrap :', response);

                if (response.status === 'success') {
                    setSelectedField(response.data);
                } else {
                    toastMessage('Failed to load field details', 'error');
                    setSelectedField(field); // Fallback to the basic field data
                }
            } catch (error) {
                console.error('Error fetching field details:', error);
                toastMessage('Error loading field details', 'error');
                setSelectedField(field); // Fallback to the basic field data
            } finally {
                setIsFieldLoading(false);
            }
        } else {
            // For create mode, just set selectedField to null
            setSelectedField(null);
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedField(null);
    };

    const handleSuccess = async (updatedField: formFieldsData) => {
        // Optimistic update of local state
        setData(prevData => {
            // If field exists, update; otherwise, add new field
            const fieldIndex = prevData.findIndex(f => f.uuid === updatedField.uuid);
            if (fieldIndex !== -1) {
                // Update existing field
                const newData = [...prevData];
                newData[fieldIndex] = updatedField;
                return newData;
            } else {
                // Add new field
                return [...prevData, updatedField];
            }
        });

        // Refresh data from server
        await fetchData({
            search_term: searchTerm,
            sort_column: sortColumn,
            sort_order: sortOrder
        });

        // Close modal
        handleCloseModal();
    };

    const handleSearch = async (term: string): Promise<void> => {
        setSearchTerm(term);
        await fetchData({
            search_term: term,
            sort_column: sortColumn,
            sort_order: sortOrder
        });
    };

    const handleSort = async (column: string, order: 'ASC' | 'DESC'): Promise<void> => {
        setSortColumn(column);
        setSortOrder(order);
        await fetchData({
            search_term: searchTerm,
            sort_column: column,
            sort_order: order
        });
    };

    // Add the handler for saving column order
    const handleSaveColumnOrder = async (orderData: { columns: { uuid: string; col_order: number }[] }): Promise<void> => {
        if (isSavingOrder) return; // Prevent multiple simultaneous saves

        setIsSavingOrder(true);
        try {
            console.log('Saving column order:', orderData);
            // Call your API function to save the order
            const response = await saveColumnOrder(orderData);

            if (response.status === 'success') {
                toastMessage('Column order saved successfully', 'success');

                // Refresh data from server to get the updated order
                await fetchData({
                    search_term: searchTerm,
                    sort_column: sortColumn,
                    sort_order: sortOrder
                });
            } else {
                toastMessage(response.message || 'Failed to save column order', 'error');
            }
        } catch (error) {
            console.error('Error saving column order:', error);
            if (error instanceof Error) {
                toastMessage(error.message, 'error');
            } else {
                toastMessage('An error occurred while saving column order', 'error');
            }
            // Re-throw to let the SortableTable component know there was an error
            throw error;
        } finally {
            setIsSavingOrder(false);
        }
    };

    return (
        <div className="h-full flex flex-col">
            <div className="sticky top-0 z-10">
                <ModernSettingHeader
                    title="Form Field Management"
                    description="Configure and customize form fields for lead capture. Manage field types, validation, and display order to optimize your data collection process."
                    buttonText="Create New Field"
                    onClick={() => handleOpenModal()}
                    icon={<FormInput className="w-6 h-6" />}
                />
            </div>
            <div className="flex-1 overflow-y-auto p-6">
                <ModernFormFieldTable
                    initialData={data}
                    onEdit={handleOpenModal}
                    onSearch={handleSearch}
                    onSort={handleSort}
                    isLoading={isLoading || isSavingOrder}
                    searchTerm={searchTerm}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    onSaveColumnOrder={handleSaveColumnOrder}
                />
            </div>

            <SettingsSlideModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={selectedField ? 'Edit Form Field' : 'Create New Field'}
                subtitle={selectedField ? 'Modify field properties and validation rules' : 'Configure a new form field for lead capture'}
                icon={<FormInput className="w-6 h-6" />}
            >
                <CreateNewFieldForm
                    onClose={handleCloseModal}
                    uuid={selectedField?.uuid}
                    initialData={selectedField}
                    onSuccess={handleSuccess}
                />
            </SettingsSlideModal>
        </div>
    );
}

